
<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import Chart from "chart.js/auto";
import { useUserStore } from "@/stores/user";
import socket from "@/services/socket";

// User store
const userStore = useUserStore();

// Datos del dashboard
const rides = ref(0);
const distance = ref(0);
const amount = ref(0);

// Referencias para las instancias de los gráficos
let doughnutChartInstance = null;
let barChartInstance = null;

// Obtener datos del dashboard del usuario
const getDashboardData = async () => {
  try {
    const response = await userStore.dashboard();
    rides.value = response.rides;
    distance.value = response.distance;
    amount.value = response.amount;
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
  }
};

// Renderizar gráfico de dona
const renderDoughnutChart = () => {
  const ctx = document.getElementById("ridesChart").getContext("2d");

  // Destruir la instancia existente si ya está creada
  if (doughnutChartInstance) {
    doughnutChartInstance.destroy();
  }

  doughnutChartInstance = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels: ["Trips Made", "Total Distance (km)", "Total Amount ($)"],
      datasets: [
        {
          label: "Activity Summary",
          data: [rides.value, distance.value, amount.value],
          backgroundColor: ["#2563EB", "#22C55E", "#EF4444"],
          hoverBackgroundColor: ["#1E40AF", "#15803D", "#B91C1C"],
          hoverOffset: 8,
        },
      ],
    },
    options: {
      responsive: true,
      cutout: "70%", // Dona más delgada
      plugins: {
        legend: {
          display: true,
          position: "top",
          labels: {
            font: {
              size: 14,
              family: "Arial, sans-serif",
            },
            color: "#4B5563",
            padding: 20,
          },
        },
        tooltip: {
          enabled: true,
        },
      },
      animation: {
        duration: 1000,
        easing: "easeOutBounce",
      },
    },
  });
};

// Renderizar gráfico de barras
const renderBarChart = () => {
  const ctx = document.getElementById("barChart").getContext("2d");

  // Destruir la instancia existente si ya está creada
  if (barChartInstance) {
    barChartInstance.destroy();
  }

  barChartInstance = new Chart(ctx, {
    type: "bar",
    data: {
      labels: ["Trips", "Distance (km)", "Amount ($)"],
      datasets: [
        {
          label: "Statistics",
          data: [rides.value, distance.value, amount.value],
          backgroundColor: ["#3B82F6", "#10B981", "#EF4444"],
          borderColor: ["#1D4ED8", "#059669", "#B91C1C"],
          borderWidth: 1,
          barThickness: 30, // Hace las barras más delgadas
        },
      ],
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
        },
        y: {
          grid: {
            color: "#E5E7EB",
          },
          ticks: {
            beginAtZero: true,
          },
        },
      },
    },
  });
};

// Inicializar datos y gráficos
const _initDashboardInfo = async () => {
  await getDashboardData();
  renderDoughnutChart();
  renderBarChart();
};

// Montar gráficos al cargar la página
onMounted(async () => {
  await _initDashboardInfo();

  // Conectar al socket
  socket.connect();

  // Escuchar eventos de Socket.IO
  socket.on("newLogin", async () => await _initDashboardInfo());
  socket.on("newRide", async () => await _initDashboardInfo());
  socket.on("newLogout", async () => await _initDashboardInfo());
  socket.on("rideStatusUpdated", async () => await _initDashboardInfo());
  socket.on("rideCancelled", async () => await _initDashboardInfo());

  console.log("socket connected:", socket.connected);
});

onUnmounted(() => {
  // Desconectar el socket al salir de la página
  socket.off("newLogin");
  socket.off("newRide");
  socket.off("newLogout");
  socket.off("rideStatusUpdated");
  socket.off("rideCancelled");
  socket.disconnect();

  // Destruir gráficos para liberar memoria
  if (doughnutChartInstance) {
    doughnutChartInstance.destroy();
  }
  if (barChartInstance) {
    barChartInstance.destroy();
  }
});
</script>

<template>
  <div class="p-6 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-200">
    <div class="max-w-6xl mx-auto">
      <!-- Resumen General -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <!-- Total de viajes -->
        <div class="bg-white dark:bg-gray-900 shadow-lg rounded-lg p-4">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Total trips</h2>
          <p class="text-4xl font-bold text-blue-500 mt-2">{{ rides }}</p>
        </div>

        <!-- Distancia total -->
        <div class="bg-white dark:bg-gray-900 shadow-lg rounded-lg p-4">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Total distance</h2>
          <p class="text-4xl font-bold text-green-500 mt-2">{{ distance }} km</p>
        </div>

        <!-- Monto total -->
        <div class="bg-white dark:bg-gray-900 shadow-lg rounded-lg p-4">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300">Total amount</h2>
          <p class="text-4xl font-bold text-red-500 mt-2">$ {{ amount }}</p>
        </div>
      </div>

      <!-- Visualización Gráfica -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Gráfico de Dona -->
        <div class="bg-white dark:bg-gray-900 shadow-lg rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Activity Summary</h2>
          <canvas id="ridesChart" class="w-full h-64"></canvas>
        </div>

        <!-- Gráfico de Barras -->
        <div class="bg-white dark:bg-gray-900 shadow-lg rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Statistics Overview</h2>
          <canvas id="barChart" class="w-full h-64"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Estilo adicional opcional */
</style>
